import { useMutation, useQuery } from '@apollo/client';
import {
	CREATE_COMP_SEASONS,
	GET_PROPERTY_COMPETITORS,
	GET_PROPERTY_COMPETITOR_SET,
	UPDATE_COMP_SEASONS,
} from '../gql/gql-prop-competitors';
import type {
	AddPropCompetitorSetData,
	DeletePropertyCompetitorSetData,
	PropCompRatesData,
	PropCompetitorSetData,
	PropCompetitorsData,
	UpdatePropertyCompetitorData,
} from '../types/prop-competitors-data';
import {
	DELETE_COMP_SEASONS,
	GET_PROP_RATES,
} from './../gql/gql-prop-competitors';

import { usePropertyContext } from 'context/propertyContext';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { PropCompRates } from 'types/RulesTypes';
import type { SearchInfo } from 'types/SearchTypes';

interface UsePropCompetitorsArgs {
	propertyId?: string;
}

export const usePropCompetitors = ({ propertyId }: UsePropCompetitorsArgs) => {
	return useQuery<PropCompetitorsData>(GET_PROPERTY_COMPETITORS, {
		skip: !propertyId,
		variables: { propertyId },
		notifyOnNetworkStatusChange: true,
	});
};

interface UsePropCompetitorSetArgs {
	propertyId?: string;
	seasonId?: string;
}

export const usePropCompetitorSet = ({
	propertyId,
	seasonId,
}: UsePropCompetitorSetArgs) => {
	return useQuery<PropCompetitorSetData>(GET_PROPERTY_COMPETITOR_SET, {
		skip: !propertyId || !seasonId,
		variables: { propertyId, seasonId },
		notifyOnNetworkStatusChange: true,
	});
};

export const useAddPropCompetitorSet = () => {
	return useMutation<AddPropCompetitorSetData>(CREATE_COMP_SEASONS, {
		refetchQueries: ['GetPropertyCompetitorSet'],
	});
};

export const useUpdatePropCompetitorSet = () => {
	return useMutation<UpdatePropertyCompetitorData>(UPDATE_COMP_SEASONS, {
		refetchQueries: ['GetPropertyCompetitorSet'],
	});
};

export const useDeletePropCompetitorSet = () => {
	return useMutation<DeletePropertyCompetitorSetData>(DELETE_COMP_SEASONS, {
		refetchQueries: ['GetPropertyCompetitorSet'],
	});
};

type CompRate = {
	comp_id: number | null;
	comp_name: string;
	comp_rate: number[];
};

type PropCompRateType = {
	stay_dates: string[];
	market_rates: number[];
	current_rates: number[];
	comp_rate: CompRate[];
};

const getFormattedCompRates = (
	propCompRates: PropCompRates[],
): PropCompRateType => {
	const propCompRate: PropCompRateType = {
		stay_dates: [],
		market_rates: [],
		current_rates: [],
		comp_rate: [],
	};
	for (const pcRate of propCompRates) {
		propCompRate.stay_dates.push(dayjs(pcRate.stay_date).format('M/D'));
		propCompRate.market_rates.push(pcRate.market_rate);
		propCompRate.current_rates.push(pcRate.current_rate);
		if (!propCompRate.comp_rate.length) {
			for (let i = 1; i <= 10; i++) {
				if (pcRate[`comp_id_${i}` as keyof typeof pcRate]) {
					const obj: CompRate = {
						comp_id: null,
						comp_name: '',
						comp_rate: [],
					};
					obj.comp_id = +pcRate[`comp_id_${i}` as keyof typeof pcRate];
					obj.comp_name = pcRate[
						`comp_name_${i}` as keyof typeof pcRate
					] as string;
					for (const rate of propCompRates) {
						obj.comp_rate.push(
							rate[`comp_rate_${i}` as keyof PropCompRates] as number,
						);
					}
					propCompRate.comp_rate.push(obj);
				}
			}
		}
	}
	return propCompRate;
};

export const useCompRatesChartData = (input: SearchInfo) => {
	const {
		property: { propertyId },
	} = usePropertyContext();
	const { data, loading, error, refetch } = useQuery<PropCompRatesData>(
		GET_PROP_RATES,
		{
			skip: !propertyId,
			fetchPolicy: 'cache-and-network',
			variables: { propertyId, input },
		},
	);

	const chartData = useMemo(
		() => getFormattedCompRates(data?.PropCompetitorSet.results || []),
		[data?.PropCompetitorSet],
	);

	return {
		chartData,
		compRatesLoading: loading,
		compRatesError: error,
		refetchCompRates: refetch,
	};
};
